import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Button, DialogActions, DialogContent, DialogTitle, FormControl, FormLabel, IconButton, MenuItem, Select, TextField } from "@mui/material";
import { Close, Send } from "@mui/icons-material";
import { t } from "i18next";
import { ReportOperationDataCommandHandler } from "../cqrs/report-operation-data-command/report-operation-data-command-handler";
import { ReportOperationDataCommand } from "../cqrs/report-operation-data-command/report-operation-data-command";
import { notificationModel } from "../../../../../common/component/notifications/notification-model";
import { OperationDataReportConfirmClose } from "./operation-data-report-confirm-close";
import { context } from "../../../../../context/context";
import { OperationDataReportIssueTypeProvider } from "./operation-data-report-issue-type-provider";

interface IOperationDataReportFormProps {
    operation: string;
    onSuccess: (ticket: string) => void;
    onClose: () => void;
}

export const OperationDataReportForm = (props: IOperationDataReportFormProps) => {
    const [contactNameValue, setContactNameValue] = useState<string>("");
    const [contactEmailValue, setContactEmailValue] = useState<string>("");
    const [contactPhoneValue, setContactPhoneValue] = useState<string>("");
    const [issueTypeValue, setissueTypeValue] = useState<string>("");
    const [descriptionValue, setDescriptionValue] = useState<string>("");
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [isFormCloseConfirmationOpen, setIsFormCloseConfirmationOpen] = useState<boolean>(false);

    const handleSubmit = useCallback(async (contactName: string, contactEmail: string,
                                            contactPhone: string, description: string) => {
        try {
            setIsSubmitting(true);
            props.onSuccess(await ReportOperationDataCommandHandler(new ReportOperationDataCommand(props.operation,
                contactName, contactEmail, contactPhone, issueTypeValue, description, context.settings.lang)));
        } catch (err) {
            notificationModel.addErrorMessage(t("components.operationDataValidation.report.popup.error"));
            setIsSubmitting(false);
        }
    }, [props.operation, contactNameValue, contactEmailValue, contactPhoneValue, descriptionValue]);

    const availableIssues = useMemo(() => OperationDataReportIssueTypeProvider(), []);
    useEffect(() => {
        setissueTypeValue(availableIssues[0] ?? "");
    }, []);

    const handleFormClose = useCallback(() => { setIsFormCloseConfirmationOpen(true); }, []);

    return <Fragment>
        {isFormCloseConfirmationOpen &&
            <OperationDataReportConfirmClose onConfirm={props.onClose}
                                             onCancel={() => setIsFormCloseConfirmationOpen(false)} />}
        <DialogTitle>
            {t("components.operationDataValidation.report.popup.title", {
                operationCode: props.operation,
            })}
        </DialogTitle>
        <IconButton onClick={handleFormClose} className={"operation-report__close"}>
            <Close />
        </IconButton>
        <DialogContent className={"operation-report__form"}>
            <div>
                <FormControl variant={"outlined"} fullWidth>
                    <FormLabel htmlFor={"ticket-issue"}>
                        {t("components.operationDataValidation.form.contactName.label")}
                    </FormLabel>
                    <TextField id={"contactName"} variant={"outlined"}
                               placeholder={t("components.operationDataValidation.form.contactName.placeholder")}
                               onChange={e => setContactNameValue(e.target.value)}
                               value={contactNameValue} />
                </FormControl>
            </div>
            <div className={"operation-report__group"}>
                <FormControl variant={"outlined"} fullWidth>
                    <FormLabel htmlFor={"ticket-issue"}>
                        {t("components.operationDataValidation.form.contactEmail.label")}
                    </FormLabel>
                    <TextField id={"contactEmail"} variant={"outlined"}
                               placeholder={t("components.operationDataValidation.form.contactEmail.placeholder")}
                               onChange={e => setContactEmailValue(e.target.value)}
                               value={contactEmailValue} />
                </FormControl>
                <FormControl variant={"outlined"} fullWidth>
                    <FormLabel htmlFor={"ticket-issue"}>
                        {t("components.operationDataValidation.form.contactPhone.label")}
                    </FormLabel>
                    <TextField id={"contactPhone"} variant={"outlined"}
                               placeholder={t("components.operationDataValidation.form.contactPhone.placeholder")}
                               onChange={e => setContactPhoneValue(e.target.value)}
                               value={contactPhoneValue} />
                </FormControl>
            </div>
            <div>
                <FormControl variant={"outlined"} fullWidth>
                    <FormLabel htmlFor={"issue"}>
                        {t("components.operationDataValidation.form.issue.label")}
                    </FormLabel>
                    <Select value={issueTypeValue} id={"issue"}>
                        {availableIssues.map(item =>
                            <MenuItem key={`issue-item-${item}`} value={item} onClick={() => setissueTypeValue(item)}>
                                {t(`components.operationDataValidation.form.issue.${item}`)}
                            </MenuItem>)}
                    </Select>
                </FormControl>
            </div>
            <div>
                <FormControl variant={"outlined"} fullWidth>
                    <FormLabel htmlFor={"ticket-description"}>
                        {t("components.operationDataValidation.form.description.label")}
                    </FormLabel>
                    <TextField multiline id={"ticket-description"} variant={"outlined"}
                               minRows={4} maxRows={4}
                               placeholder={t("components.operationDataValidation.form.description.placeholder")}
                               onChange={e => setDescriptionValue(e.target.value)}
                               value={descriptionValue} />
                </FormControl>
            </div>
        </DialogContent>
        <DialogActions>
            <Button onClick={handleFormClose} variant={"contained"} color={"inherit"}>Cancel</Button>
            <Button onClick={() => handleSubmit(contactNameValue, contactEmailValue, contactPhoneValue, descriptionValue)}
                    variant={"contained"} color={"secondary"} endIcon={<Send />} loading={isSubmitting} loadingPosition={"end"}
                    disabled={isSubmitting || !descriptionValue}>
                {t("components.operationDataValidation.form.submitButton")}
            </Button>
        </DialogActions>
    </Fragment>;
};
